import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/beui';
import {
  MoreHorizontal,
  ArrowUpRight,
  Edit3,
  Copy,
  Share2,
  Star,
  Folder,
  Trash2,
  Check,
} from '@/components/icons';
import type { DiagramMetadata, Folder as FolderType } from '../../types';
import { DiagramIcon } from '../diagramIcons';

interface DiagramRowMenuProps {
  diagram: DiagramMetadata;
  folders: FolderType[];
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onToggleStar: () => void;
  onMove: (folderId: string | null) => void;
  onShare: () => void;
  onDelete: () => void;
}

/** Per-row actions menu for the diagram index. Accessible (BEUI) — keyboard + ESC + focus. */
export function DiagramRowMenu({
  diagram,
  folders,
  onOpen,
  onRename,
  onDuplicate,
  onToggleStar,
  onMove,
  onShare,
  onDelete,
}: DiagramRowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <button
          onClick={(e) => e.stopPropagation()}
          aria-label={`Actions for ${diagram.name}`}
          className="ss-press grid h-7 w-7 place-items-center rounded text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring aria-expanded:bg-muted aria-expanded:text-foreground"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[188px]" ariaLabel={`Actions for ${diagram.name}`}>
        <DropdownMenuItem onSelect={onOpen}>
          <ArrowUpRight className="opacity-80" /> Open
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onRename}>
          <Edit3 className="opacity-80" /> Rename &amp; settings…
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDuplicate}>
          <Copy className="opacity-80" /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onShare}>
          <Share2 className="opacity-80" /> Share…
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onToggleStar}>
          <Star
            className={diagram.isStarred ? 'fill-amber-500 text-amber-500' : 'opacity-80'}
          />
          {diagram.isStarred ? 'Remove star' : 'Star'}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span className="flex items-center gap-2.5">
              <Folder className="opacity-80" /> Move to
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-[188px]" ariaLabel="Move to folder">
            <DropdownMenuItem
              onSelect={() => onMove(null)}
              disabled={!diagram.folderId}
            >
              <span className="flex-1">No folder</span>
              {!diagram.folderId && <Check className="text-primary" />}
            </DropdownMenuItem>
            {folders.length > 0 && <DropdownMenuSeparator />}
            {folders.map((f) => {
              const active = diagram.folderId === f.id;
              return (
                <DropdownMenuItem
                  key={f.id}
                  onSelect={() => onMove(f.id)}
                  disabled={active}
                >
                  <DiagramIcon name={f.icon} color={f.color} className="h-4 w-4 flex-none" />
                  <span className="flex-1 truncate">{f.name}</span>
                  {active && <Check className="text-primary" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem tone="destructive" onSelect={onDelete}>
          <Trash2 /> Delete…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
